import {Component, OnInit, ViewChild} from '@angular/core';
import {ROUTES} from "../../../routes";
import {MembersService} from "../../../services/members.service";
import {IUserDetail} from "../../../models/user-detail.model";
import {ToastrService} from 'ngx-toastr';
import moment from "moment";
import {escape} from "lodash-es";
import {DateWithHourFormat} from "../../../shared/constants/date-with-hour-format";
import {CurrentUserService} from "../../../services/current-user.service";
import {MemberLogic} from "../../../models/member-logic";
import {OrderByPipe} from '../../../order-by.pipe';
import {IUserDeletionPreview} from "../../../models/user-deletion-preview.model";
import {GenericModalComponent} from "../../../shared/generic-modal-component/generic-modal-component.component";
import {TextInputModalComponent} from "../../../shared/text-input-modal/text-input-modal.component";

@Component({
        selector: 'eil-member-list',
        templateUrl: './member-list.component.html',
        styleUrls: ['./member-list.component.scss'],
        standalone: false
    }
)
export class MemberListComponent implements OnInit {
    @ViewChild(GenericModalComponent) genericModal!: GenericModalComponent;
    @ViewChild(TextInputModalComponent) textInputModal!: TextInputModalComponent;

    // @Input() public inputExample:    string;

    // public publicExample:            string;
    // public anotherExample:           any;

    // private privateExample:          boolean;
    currentUser: IUserDetail;
    linkToEdit: string;
    loading = false;
    memberLogic = new MemberLogic();
    members = [];
    order: string = "getStatus(member)";
    reverse: boolean = false;
    searchText: string = "";
    tablePage: number;

    deletePreview: IUserDeletionPreview = {};

    constructor(private membersService: MembersService, private toastrService: ToastrService, private currentUserService: CurrentUserService, orderByPipe: OrderByPipe) {
        this.linkToEdit = `/${ROUTES.ADMIN}/${ROUTES.ADMIN_EDITMEMBER}/`;
        this.loading = true;
        // TODO: filter?
        this.membersService.getAll((success, result, error) => {
            // unflag wait
            this.loading = false;
            if (success) {
                this.members = result;
            } else if (error && !error.displayHasBeenHandled) {
                toastrService.error(error.errorMessage);
            }
        });
        this.tablePage = 1; //Start at the first page
        orderByPipe.transform(this.members, "getStatus(member)")
    }

    // TODO: put this in a common library!
    getAgo(dateString) {
        if (!dateString) {
            return '--';
        }
        return moment(dateString).fromNow();
    }

    toggleMemberSetting(member: IUserDetail, valueToSet: boolean, settingName: string) {

        if (confirm(`Change ${settingName} for ${member.firstName} ${member.lastName} to ${valueToSet}?`)) {
            this.loading = true;
            this.membersService.setMemberSetting(member.email, settingName, valueToSet,
                (success, result, error) => {
                    if (success) {
                        let index = this.members.indexOf(member);
                        if (index < 0) {
                            this.toastrService.warning("member not found in list...");
                        } else {
                            this.members[index] = result;
                        }
                    } else if (!error.displayHasBeenHandled) {
                        this.toastrService.error(error.errorMessage);
                    }
                    // unflag wait
                    this.loading = false;
                });
        }
    }

    reject(member: IUserDetail) {
        if (confirm(`This will reject ${member.firstName} ${member.lastName}.  Are you sure?`)) {
            this.loading = true;
            this.membersService.reject(member.email,
                (success, error) => {
                    if (success) {
                        let index = this.members.indexOf(member);
                        if (index < 0) {
                            this.toastrService.warning("member not found in list...");
                        } else {
                            this.members.splice(index, 1);
                        }
                    } else if (!error.displayHasBeenHandled) {
                        this.toastrService.error(error.errorMessage);
                    }
                    // unflag wait
                    this.loading = false;
                });
        }
    }

    delete(member: IUserDetail) {
        this.loading = true;
        this.membersService.getDeletePreview(member.email,
            (success, result, error) => {
                this.loading = false;

                if (!success) {
                    this.toastrService.error(error?.errorMessage ?? 'Unable to load delete preview.');
                    return;
                }

                this.deletePreview = result ?? {};

                if ((result?.applicationsInThePastThreeYears ?? []).length == 0) {
                    this.promptForDeleteReason(member);
                    return;
                }

                this.genericModal.show({
                    title: `Delete ${member.email}?`,
                    content: this.buildDeletePreviewHtml(member),
                    confirmText: 'Delete',
                    cancelText: 'Cancel',
                    showCancelButton: true,
                    onConfirm: () => this.promptForDeleteReason(member),
                    onCancel: () => {
                    }
                });
            });
    }

    deactivate(member: IUserDetail) {
        // TODO: something clearer
        this.toggleMemberSetting(member, false, "IsApproved");
    }

    reactivate(member: IUserDetail) {
        // TODO: something clearer
        this.toggleMemberSetting(member, true, "IsApproved");
    }

    toggleIsRegistered(member: IUserDetail, valueToSet: boolean) {
        this.toggleMemberSetting(member, valueToSet, "IsRegistered");
    }

    toggleIsAuditor(member: IUserDetail, valueToSet: boolean) {
        this.toggleMemberSetting(member, valueToSet, "IsAuditor");
    }

    toggleIsApproved(member: IUserDetail, valueToSet: boolean) {
        this.toggleMemberSetting(member, valueToSet, "IsApproved");
    }

    toggleIsMember(member: IUserDetail, valueToSet: boolean) {
        this.toggleMemberSetting(member, valueToSet, "IsMember");
    }

    toggleIsAdmin(member: IUserDetail, valueToSet: boolean) {
        this.toggleMemberSetting(member, valueToSet, "IsAdmin");
    }

    ngOnInit() {
        // TODO: flag wait?
        this.currentUserService.currentUser.subscribe(user => {
            this.currentUser = user;
        });
    }

    setOrder(value: string) {
        if (this.order === value) {
            this.reverse = !this.reverse;
        }

        this.order = value;
    }

    private buildDeletePreviewHtml(member: IUserDetail): string {
        const items = this.deletePreview.applicationsInThePastThreeYears ?? [];

        const list = items.map(app => {
            const eventName = escape(app.eventName ?? 'Unnamed event');
            const status = escape(app.status ?? 'Unknown');
            const momentDate = moment(app.createdAt);
            const createdAtFormattedDate = momentDate.format(DateWithHourFormat);
            return `<ul><li><strong>Name: </strong>${eventName} </li><li><strong>Status: </strong>${status}</li><li><strong>Created at: </strong>${createdAtFormattedDate}</li></ul>`;
        }).join('');

        return `
            <p>WARNING: This user has made the following application requests in the past three years:</p>
            ${list}
            <p>Do you want to continue with the delete anyway?</p>
        `;
    }

    private promptForDeleteReason(member: IUserDetail) {
        this.textInputModal.show({
            title: `WARNING: This action is irreversible. Please provide a reason for deleting ${member.email}}`,
            placeholder: 'Enter the reason for this deletion (required)',
            isTextArea: true,
            required: true,
            confirmText: 'Delete',
            confirmButtonClass: 'btn btn-danger',
            onConfirm: (reason: string) => {
                this.textInputModal.hide();
                this.deleteMember(member, reason.trim());
            }
        });
    }

    private deleteMember(member: IUserDetail, reason: string) {
        this.loading = true;
        this.membersService.deleteUser(member.email, reason, (success, error) => {
            if (success) {
                const index = this.members.indexOf(member);
                if (index >= 0) {
                    this.members.splice(index, 1);
                }
            } else if (!error.displayHasBeenHandled) {
                this.toastrService.error(error.errorMessage);
            }
            this.loading = false;
        });
    }
}
